"use client";

import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

/**
 * Aufklappmenü mit React-Zustand (Plan Regel 10: Elemente mit Daten-/Server-Bezug als React-Baustein,
 * Preline-Markup unverändert). Die Menü-Fläche und die Einträge tragen die Klassen des Preline-Dropdowns;
 * Öffnen/Schließen steuert React statt `hs-dropdown`, weil Karten und Spalten im Brett zur Laufzeit
 * (neu) eingehängt werden und Preline-Instanzen an ersetzten Elementen verloren gingen.
 *
 * Die Fläche hängt (wie bei Preline-Popper) außerhalb des scrollenden Brett-Containers am Dokument
 * (`position: fixed`) und weicht nach oben aus, wenn unten kein Platz ist. Die Koordinaten setzt dieser
 * Baustein zur Laufzeit am Element (wie das Preline-Skript selbst); sie sind keine Gestaltung.
 *
 * Bedienung: Klick auf den Auslöser öffnet/schließt, Escape schließt und gibt den Fokus zurück,
 * Klick außerhalb und Scrollen/Größenänderung schließen, Pfeil hoch/runter wandert durch die Einträge
 * (`role="menuitem"`/`menuitemradio`).
 */
export interface DropdownMenueApi {
  schliessen: () => void;
}

/** Gemeinsame Eintrags-Klassen (aus dem Preline-Dropdown), für Aufrufer, die eigene Einträge setzen. */
export const MENUE_EINTRAG =
  "w-full flex items-center gap-x-3.5 py-2 px-3 rounded-lg text-sm text-start text-dropdown-item-foreground hover:bg-dropdown-item-hover focus:outline-hidden focus:bg-dropdown-item-focus disabled:opacity-50 disabled:pointer-events-none";
export const MENUE_EINTRAG_GEFAHR =
  "w-full flex items-center gap-x-3.5 py-2 px-3 rounded-lg text-sm text-start text-destructive hover:bg-dropdown-item-hover focus:outline-hidden focus:bg-dropdown-item-focus disabled:opacity-50 disabled:pointer-events-none";

export const MENUE_FLAECHE =
  "fixed top-0 start-0 z-70 bg-dropdown border border-dropdown-line rounded-lg shadow-md divide-y divide-dropdown-divider";
const ABSTAND = 8;

/**
 * Ebenen: Seite z-70; Modale z-70 (Hintergrund) / z-80 (Hülle), gestapeltes Modal z-81 / z-82; Tour z-90.
 * Liegt der Auslöser in einem Modal, rückt die Fläche auf z-85 (über dem obersten Modal, unter der Tour).
 */
const EBENE_SEITE = "z-70";
const EBENE_IM_MODAL = "z-85";
const MODAL_HUELLE = '[role="dialog"][aria-modal="true"]';

export function DropdownMenue({
  ausloeserKlasse,
  ausloeserLabel,
  ausloeser,
  rechts = true,
  breit = false,
  seitlich = false,
  flaecheKlasse = MENUE_FLAECHE,
  ausloeserTitel,
  beimOeffnen,
  tabSchliesst = true,
  fokusAuswahl,
  children,
}: {
  /** Klassen des Auslöser-Knopfs (vollständige Kette, vom Aufrufer). */
  ausloeserKlasse: string;
  /** Zugänglicher Name des Auslösers (Pflicht, wenn der Auslöser nur ein Symbol ist). */
  ausloeserLabel: string;
  /** Inhalt des Auslösers (Symbol und/oder Text). */
  ausloeser: React.ReactNode;
  /** Menü rechtsbündig zum Auslöser statt linksbündig. */
  rechts?: boolean;
  /** Auslöser über die volle Breite des umgebenden Blocks (z. B. in einer Seitenleiste). */
  breit?: boolean;
  /** Menü seitlich neben dem Auslöser statt darunter (Preline `right-start`), z. B. aus einer schmalen Seitenleiste; randbewusst. Vorrang vor `rechts`. */
  seitlich?: boolean;
  /** Klassen der Menü-Fläche, wenn die Vorlage des Blocks eine andere Fläche trägt als das Standard-Dropdown (z. B. `rounded-xl shadow-lg`). Muss `fixed top-0 start-0 z-70` enthalten; liegt der Auslöser in einem Modal, tauscht der Baustein `z-70` gegen `z-85`. */
  flaecheKlasse?: string;
  /** Kurzer Hinweistext beim Daraufzeigen (`title`), wo der Auslöser nur ein Symbol zeigt. */
  ausloeserTitel?: string;
  beimOeffnen?: () => void;
  /**
   * `true` (Standard): Tab in der Fläche schließt das Menü und setzt den Fokus hinter dem Auslöser fort (reine Eintragsmenüs).
   * `false` für Menüs mit Bedienelementen (Reiter, Schalter, Suchfeld, Dev-Menü M-057): Tab wandert dann durch die Fläche.
   */
  tabSchliesst?: boolean;
  /** CSS-Auswahl des Elements, das beim Öffnen den Fokus bekommt; Standard: der erste aktive Menüeintrag. */
  fokusAuswahl?: string;
  /** Inhalt; die Breite bestimmt der Inhalt selbst (z. B. ein Block mit `w-64`). */
  children: (api: DropdownMenueApi) => React.ReactNode;
}) {
  const [offen, setOffen] = useState(false);
  const [imModal, setImModal] = useState(false);
  const knopf = useRef<HTMLButtonElement>(null);
  const menue = useRef<HTMLDivElement>(null);
  const id = useId();

  const schliessen = useCallback(() => setOffen(false), []);

  // Platzieren vor dem ersten Zeichnen und nach jeder Inhaltsänderung (Untermenü ändert die Größe).
  useLayoutEffect(() => {
    if (!offen) return;
    const m = menue.current;
    const k = knopf.current;
    if (!m || !k) return;
    const a = k.getBoundingClientRect();
    const b = m.getBoundingClientRect();
    const vw = document.documentElement.clientWidth;
    const vh = window.innerHeight;
    let links: number;
    let oben: number;
    if (seitlich) {
      // Preline `[--placement:right-start]`: rechts neben dem Auslöser, Oberkante bündig; ohne Platz rechts links daneben,
      // und nie über den unteren Bildschirmrand hinaus.
      links = a.right + ABSTAND;
      if (links + b.width > vw - ABSTAND && a.left - ABSTAND - b.width >= ABSTAND) links = a.left - ABSTAND - b.width;
      oben = Math.min(a.top, vh - ABSTAND - b.height);
    } else {
      links = rechts ? a.right - b.width : a.left;
      oben = a.bottom + ABSTAND;
      if (oben + b.height > vh - ABSTAND && a.top - ABSTAND - b.height > ABSTAND) oben = a.top - ABSTAND - b.height;
    }
    links = Math.max(ABSTAND, Math.min(links, vw - b.width - ABSTAND));
    m.style.left = `${Math.round(links)}px`;
    m.style.top = `${Math.round(Math.max(ABSTAND, oben))}px`;
    // Fokus in die Fläche holen, wenn er außerhalb liegt (beim Öffnen) oder verloren ging (Untermenü
    // ersetzt den fokussierten Eintrag); sonst bliebe die Fläche am Dokumentende ohne Tastaturzugang.
    if (!m.contains(document.activeElement)) {
      m.querySelector<HTMLElement>(fokusAuswahl ?? '[role^="menuitem"]:not(:disabled)')?.focus({ preventScroll: true });
    }
  });

  useEffect(() => {
    if (!offen) return;
    const beiKlick = (e: PointerEvent) => {
      const ziel = e.target as Node;
      if (menue.current?.contains(ziel) || knopf.current?.contains(ziel)) return;
      setOffen(false);
    };
    const beiTaste = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        setOffen(false);
        knopf.current?.focus();
      }
    };
    const beiScroll = (e: Event) => {
      if (menue.current && e.target instanceof Node && menue.current.contains(e.target)) return;
      setOffen(false);
    };
    const beiGroesse = () => setOffen(false);
    document.addEventListener("pointerdown", beiKlick);
    document.addEventListener("keydown", beiTaste);
    document.addEventListener("scroll", beiScroll, true);
    window.addEventListener("resize", beiGroesse);
    return () => {
      document.removeEventListener("pointerdown", beiKlick);
      document.removeEventListener("keydown", beiTaste);
      document.removeEventListener("scroll", beiScroll, true);
      window.removeEventListener("resize", beiGroesse);
    };
  }, [offen]);

  const pfeilNavigation = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Tab") {
      // Die Fläche hängt am Dokumentende: Tab schließt und setzt den Fokus hinter den Auslöser fort (außer bei `tabSchliesst={false}`).
      if (!tabSchliesst) return;
      setOffen(false);
      knopf.current?.focus();
      return;
    }
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    const eintraege = Array.from(e.currentTarget.querySelectorAll<HTMLElement>('[role^="menuitem"]:not(:disabled)'));
    if (eintraege.length === 0) return;
    e.preventDefault();
    const i = eintraege.indexOf(document.activeElement as HTMLElement);
    const n = e.key === "ArrowDown" ? (i + 1) % eintraege.length : (i - 1 + eintraege.length) % eintraege.length;
    eintraege[n]?.focus();
  };

  return (
    <div className={breit ? "relative flex w-full" : "relative inline-flex"}>
      {/* Preline: component/dropdown/dropdown-dividers */}
      <button
        ref={knopf}
        type="button"
        id={`${id}-knopf`}
        className={ausloeserKlasse}
        aria-haspopup="menu"
        aria-expanded={offen}
        aria-controls={offen ? `${id}-menue` : undefined}
        aria-label={ausloeserLabel}
        title={ausloeserTitel}
        onClick={() => {
          if (!offen) {
            beimOeffnen?.();
            setImModal(knopf.current?.closest(MODAL_HUELLE) != null);
          }
          setOffen((o) => !o);
        }}
      >
        {ausloeser}
      </button>
      {offen &&
        createPortal(
          <div
            ref={menue}
            id={`${id}-menue`}
            className={imModal ? flaecheKlasse.replace(EBENE_SEITE, EBENE_IM_MODAL) : flaecheKlasse}
            role="menu"
            aria-orientation="vertical"
            aria-labelledby={`${id}-knopf`}
            onKeyDown={pfeilNavigation}
          >
            {children({ schliessen })}
          </div>,
          document.body,
        )}
    </div>
  );
}
